/**
 * Decimal helpers that never touch a JS number.
 *
 * CONTRACT §"Never `Number()` a dose amount": amounts are strings from the API
 * to the input and back. 0.1 + 0.2 = 0.30000000000000004 is a rounding curio in
 * a shopping cart and a wrong dose here, so all arithmetic below is BigInt over
 * a common scale.
 *
 * The shared `Decimal` zod type is `/^\d{1,10}(\.\d{1,4})?$/` — non-negative,
 * at most 4 decimal places. These helpers accept a leading `-` so an
 * intermediate subtraction can be detected and clamped rather than silently
 * wrapping, but `normalizeDecimal` will never hand one back to the API.
 */

export const MAX_DECIMAL_PLACES = 4;

const DECIMAL_RE = /^-?\d*(\.\d*)?$/;

export interface ParsedDecimal {
  /** Value scaled to an integer: "1.25" -> 12500n at scale 4. */
  units: bigint;
  /** Number of decimal places the integer is scaled by. */
  scale: number;
}

/** True for anything `parseDecimal` can read. Empty string is not valid. */
export function isDecimalLike(value: string): boolean {
  if (value === '' || value === '-' || value === '.' || value === '-.') return false;
  return DECIMAL_RE.test(value);
}

/** Parse to scaled BigInt, or null if the string is not a decimal. */
export function parseDecimal(value: string, scale = MAX_DECIMAL_PLACES): ParsedDecimal | null {
  if (!isDecimalLike(value)) return null;

  const negative = value.startsWith('-');
  const unsigned = negative ? value.slice(1) : value;
  const dot = unsigned.indexOf('.');
  const whole = dot === -1 ? unsigned : unsigned.slice(0, dot);
  const fracRaw = dot === -1 ? '' : unsigned.slice(dot + 1);

  // More precision than we carry is truncated, not rounded: rounding a dose up
  // is a decision the UI is not entitled to make.
  const frac = fracRaw.slice(0, scale).padEnd(scale, '0');
  const digits = `${whole === '' ? '0' : whole}${frac}`;

  let units: bigint;
  try {
    units = BigInt(digits);
  } catch {
    return null;
  }
  return { units: negative ? -units : units, scale };
}

/** Render a scaled BigInt back to a plain decimal string with no trailing zeros. */
export function formatParsed({ units, scale }: ParsedDecimal): string {
  const negative = units < 0n;
  const abs = negative ? -units : units;
  const divisor = 10n ** BigInt(scale);
  const whole = (abs / divisor).toString();
  let frac = (abs % divisor).toString().padStart(scale, '0').replace(/0+$/, '');
  if (frac === '') return `${negative ? '-' : ''}${whole}`;
  // Guard against a scale of 0 producing "1." — padStart above already handles it.
  frac = frac.slice(0, scale);
  return `${negative ? '-' : ''}${whole}.${frac}`;
}

/**
 * Canonical form of a decimal string: "2.00" -> "2", "0.50" -> "0.5",
 * ".5" -> "0.5". Returns null for anything unparseable.
 */
export function normalizeDecimal(value: string): string | null {
  const parsed = parseDecimal(value);
  return parsed ? formatParsed(parsed) : null;
}

/** -1, 0 or 1. Returns null if either side is unparseable. */
export function compareDecimal(a: string, b: string): -1 | 0 | 1 | null {
  const pa = parseDecimal(a);
  const pb = parseDecimal(b);
  if (!pa || !pb) return null;
  if (pa.units < pb.units) return -1;
  if (pa.units > pb.units) return 1;
  return 0;
}

/** a + b, exactly. Returns null if either side is unparseable. */
export function addDecimal(a: string, b: string): string | null {
  const pa = parseDecimal(a);
  const pb = parseDecimal(b);
  if (!pa || !pb) return null;
  return formatParsed({ units: pa.units + pb.units, scale: MAX_DECIMAL_PLACES });
}

/** a - b, exactly. May return a negative string; callers clamp. */
export function subtractDecimal(a: string, b: string): string | null {
  const pa = parseDecimal(a);
  const pb = parseDecimal(b);
  if (!pa || !pb) return null;
  return formatParsed({ units: pa.units - pb.units, scale: MAX_DECIMAL_PLACES });
}

/** Clamp into [min, max]; either bound may be omitted. */
export function clampDecimal(
  value: string,
  min?: string | null,
  max?: string | null,
): string {
  let out = value;
  if (min != null && min !== '') {
    const c = compareDecimal(out, min);
    if (c === -1) out = min;
  }
  if (max != null && max !== '') {
    const c = compareDecimal(out, max);
    if (c === 1) out = max;
  }
  return out;
}

/** True when the value is exactly zero. */
export function isZeroDecimal(value: string): boolean {
  const parsed = parseDecimal(value);
  return parsed ? parsed.units === 0n : false;
}

/**
 * Display form for a dose amount. Same as `normalizeDecimal` but falls back to
 * the raw string instead of null, because a display helper must never render
 * "null" onto a medication card.
 */
export function formatDecimalForDisplay(value: string): string {
  return normalizeDecimal(value) ?? value;
}
